import React, { FC, useCallback, useEffect, useState } from 'react';
import { appendSourceQueryToProductLink } from '@ldp/media-utils';
import { useFeatureFlags, useSettingsContext } from '@ldp/common-contexts';
import { useSDMCartContext, useSDMRouterContext } from '@ldp/sdm-contexts';
import { useRedirectLink } from '../../utils';
import { IMediaShoppableDisplayProps } from './media-shoppable-display-component.interface';
import { ISDMProductTileData, SDMLanguageType } from '@ldp/common-types';
import { ShoppableDisplay } from '@ldp-web/component-library/ui/organisms/shoppable-display';
import { TrackingFnContext, trackMediaAnalyticsEvent, useAddToCartTrackEvent } from '@ldp/sdm-analytics-utils';
import { paramCase, useTrackMediaAnalyticsAdClick } from '@ldp/media-analytics';
import { IProductTile } from '@ldp-web/component-library/ui/molecules/product-tile';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { useGetA11yCartMessages } from '../../hooks';
import { IMediaWrapperComponentProps, MediaWrapperComponent } from '../media-wrapper/media-wrapper.component';
import { WrapperSection } from '@ldp-web/component-library/ui/atoms/section';
import { ShoppableDisplaySkeleton } from '@ldp-web/component-library/ui/skeletons/shoppable-display-skeleton';
import { useViewResolverContext } from '@ldp/view-resolver-lite';

export interface IMediaShoppableDisplayComponentProps extends IMediaShoppableDisplayProps {
  componentId: string;
  contentId: string;
}

export interface TShoppableTrackingContext {
  tags: unknown;
  data: {
    event: string;
    creativeOverride?: string;
  };
}

/** Media  Next Gen Shoppable Display Component */
const MediaShoppableDisplayComponentInner: FC<IMediaShoppableDisplayComponentProps> = (
  props: IMediaShoppableDisplayComponentProps
) => {
  const { locale } = useSettingsContext();
  const { addToCart, entries, isLoading: isCartLoading } = useSDMCartContext();
  const { router } = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const a11yCartMessages = useGetA11yCartMessages();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow } = getFeatureFlags();
  const trackFn = useAddToCartTrackEvent(EnableHeliosSnowplow);
  const trackMediaAnalyticsAdClick = useTrackMediaAnalyticsAdClick();
  const i18n = useSimpleTranslation();

  const {
    isSponsored,
    sponsoredText,
    headline,
    logoImage,
    mobileImage,
    tabletImage,
    desktopImage,
    callToAction,
    displayText1,
    displayText2,
    productTiles,
    componentId,
    contentId,
  } = props;

  const trackingFn = useCallback((_event: string, context: unknown | TShoppableTrackingContext = undefined): void => {
    trackMediaAnalyticsAdClick(props as unknown as Record<string, unknown>, undefined, {
      creative_id: (context as any)?.data?.creativeOverride || paramCase(callToAction?.text || ''),
    });
  }, []);

  const compData = {
    id: contentId,
    type: componentId,
    name: headline || '',
  };

  const updatedProductTiles = productTiles?.map((product: IProductTile) => {
    const { isComplementarySponsored, isSponsored: isSponsoredProduct, link } = product;
    const updatedLinkWithSource = appendSourceQueryToProductLink(
      isComplementarySponsored,
      isSponsoredProduct || isSponsored || !!sponsoredText,
      link
    );
    return {
      ...product,
      link: updatedLinkWithSource,
    };
  });

  // Create a wrapper over the tracking function to pass productTiles
  const trackFnWrapper = useCallback<TrackingFnContext>(
    (type, context) => {
      trackMediaAnalyticsEvent(
        type,
        context,
        updatedProductTiles as unknown as ISDMProductTileData[],
        compData,
        EnableHeliosSnowplow,
        trackFn,
        trackMediaAnalyticsAdClick,
        undefined
      );
    },
    [updatedProductTiles, compData, EnableHeliosSnowplow]
  );

  const inputProps = {
    language: locale as SDMLanguageType,
    isSponsored,
    sponsoredText,
    headline,
    logoImage,
    mobileImage,
    tabletImage,
    desktopImage,
    displayText1,
    displayText2,
    productTiles: updatedProductTiles as any,
    addToCartCB: addToCart,
    isCartLoading,
    entries,
    isMedia: true,
    callToAction,
    a11yMessages: a11yCartMessages,
    i18nskipText: i18n('skip'),
    trackFnWrapper,
    trackingFn,
    tileAction: redirectToPage,
  };

  return <ShoppableDisplay contentId={contentId} {...inputProps} />;
};

const isSSR = typeof document === 'undefined' || typeof window === 'undefined';

const MediaShoppableDisplayComponentInnerWrapped = MediaWrapperComponent(MediaShoppableDisplayComponentInner);

export const MediaShoppableDisplayComponent = (props: JSX.IntrinsicAttributes & object & IMediaWrapperComponentProps) => {
  const [isClient, setIsClient] = useState(false);
  const { fetchOptions } = useViewResolverContext();

  useEffect(() => {
    if (!isSSR) {
      setIsClient(true);
    }
  }, []);

  if (isSSR || !isClient || !fetchOptions) {
    return (
      <WrapperSection>
        <ShoppableDisplaySkeleton />
      </WrapperSection>
    );
  }

  return <MediaShoppableDisplayComponentInnerWrapped {...props} />;
};